import { Button, Heading, Table, VerticalSpace, Popover } from '@elhub/ds-components'
import { Warning, Check, Close, Info } from '@elhub/ds-icons'

import React, { useRef, useState } from 'react'

import { Dataset } from '../../types/meta'
import tokens from '@elhub/ds-tokens/dist/tokens.json'

interface DatasetTableProps {
  datasets: Dataset[]
  download?: boolean
  entity: string
}

const DatasetTable: React.FC<DatasetTableProps> = ({ datasets, download: isDownload = false, entity }) => {
  const formattedEntity = entity.replace(/-/g, '\u2011') // replacing the regular hyphen with a no-break hyphen

  const buttonRef = useRef(null)
  const [open, setOpen] = useState<boolean>(false)

  return (
    <>
      <Heading size='small'>{isDownload ? entity : 'Available Datasets'}</Heading>
      <Table size='small'>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader scope='col' />
            <Table.ColumnHeader scope='col'>Dataset</Table.ColumnHeader>
            {isDownload
              ? (
                <Table.ColumnHeader scope='col'>Download</Table.ColumnHeader>
                )
              : (
                <>
                  <Table.ColumnHeader scope='col'>Access Type</Table.ColumnHeader>
                  <Table.ColumnHeader scope='col'>Filter Parameter</Table.ColumnHeader>
                  <Table.ColumnHeader scope='col'>Filter Values</Table.ColumnHeader>
                  <Table.ColumnHeader scope='col'>Maximum Allowed Data Range</Table.ColumnHeader>
                  <Table.ColumnHeader scope='col'>Exportable</Table.ColumnHeader>
                  <Table.ColumnHeader scope='col'>
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      Available&nbsp;at
                      <Info
                        ref={buttonRef}
                        style={{ height: '20px', width: '20px', color: tokens['eds-color-blue'], margin: '0 8px 5px 0' }}
                        onMouseOver={() => setOpen(true)}
                        onMouseOut={() => setOpen(false)}
                      />
                      <Popover
                        open={open}
                        arrow={false}
                        offset={10}
                        onClose={() => setOpen(false)}
                        anchorEl={buttonRef.current}
                      >
                        <Popover.Content>
                          This shows whether the dataset is available at /{entity} (All) or /{entity}/{'{id}'} (One) or both paths.
                        </Popover.Content>
                      </Popover>
                    </span>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader scope='col'>Access to Scopes</Table.ColumnHeader>
                </>
                )}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {datasets.map(
            ({ name, openDataSet, maxDateRange, filterParams, filterValues, columns, scopes, exportable, routeAvailability }) => (
              <Table.ExpandableRow
                key={name}
                content={
                  <Table size='small'>
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader scope='col'>Attribute</Table.ColumnHeader>
                        <Table.ColumnHeader scope='col'>Type</Table.ColumnHeader>
                        <Table.ColumnHeader scope='col'>Description</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {columns.map(({ name, type, description }) => (
                        <Table.Row key={name}>
                          <Table.HeaderCell scope='row'>{name}</Table.HeaderCell>
                          <Table.DataCell>{type}</Table.DataCell>
                          <Table.DataCell>{description}</Table.DataCell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                }
              >
                <Table.HeaderCell scope='row' style={{ width: isDownload ? '100%' : '350px', overflowWrap: 'anywhere' }}>
                  {name}
                </Table.HeaderCell>
                {isDownload
                  ? (
                    <a href={`https://api.elhub.no/energy-data/v0/${entity}.csv?dataset=${name}`} download='download'>
                      <Button>.csv</Button>
                    </a>
                    )
                  : (
                    <>
                      <Table.DataCell style={{ width: '110px' }}>
                        {!openDataSet
                          ? (
                            <>
                              <Warning style={{ height: '20px', width: '20px', color: 'red', margin: '0 8px 5px 0' }} />
                              Restricted
                            </>
                            )
                          : 'Open'}
                      </Table.DataCell>
                      <Table.DataCell>{filterParams}</Table.DataCell>
                      <Table.DataCell>{filterValues}</Table.DataCell>
                      <Table.DataCell>{maxDateRange}</Table.DataCell>
                      <Table.DataCell>
                        {exportable
                          ? (
                            <Check style={{ height: '20px', width: '20px', color: 'green', margin: '0 8px 5px 0' }} />
                            )
                          : (
                            <Close style={{ height: '20px', width: '20px', color: 'red', margin: '0 8px 5px 0' }} />
                            )}
                      </Table.DataCell>
                      <Table.DataCell>
                        {routeAvailability === 'ALL'
                          ? `/${formattedEntity}, /${formattedEntity}/{id}`
                          : routeAvailability === 'BASE'
                            ? `/${formattedEntity}`
                            : routeAvailability === 'ID'
                              ? `/${formattedEntity}/{id}`
                              : ''}
                      </Table.DataCell>
                      <Table.DataCell>{(scopes ?? []).join(', ')}</Table.DataCell>
                    </>
                    )}
              </Table.ExpandableRow>
            )
          )}
        </Table.Body>
      </Table>
      <VerticalSpace size='3' />
    </>
  )
}

export default DatasetTable
